import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import ProductCard from './ProductCard';
import Loader from '../Loader';
import { buildApiUrl } from '../../utils/api';
import { normalizeProductCard } from '../../utils/normalizeProductCard';

const TopPriorityProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const fetch_ = async () => {
      setLoading(true);
      try {
        const res = await fetch(buildApiUrl('/api/v1/customer/products/top-priority?limit=12'), {
          headers: { 'Content-Type': 'application/json' },
        });
        const json = await res.json();
        if (!res.ok || !json.success) throw new Error();
        if (alive) setProducts((json.data || []).map(normalizeProductCard));
      } catch {
        if (alive) setProducts([]);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetch_();
    return () => { alive = false; };
  }, []);

  if (!loading && products.length === 0) return null;

  return (
    <section className="overflow-hidden bg-gradient-to-b from-primary-50/60 to-white py-6 sm:py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Accent bar */}
            <div className="h-8 w-1 rounded-full bg-primary-500" />
            <div>
              <span className="block text-[11px] font-bold uppercase tracking-[0.2em] text-primary-600">
                🔥 Today's Picks
              </span>
              <h2 className="text-xl font-black tracking-tight text-gray-900 sm:text-2xl">
                Top Priority Products
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/products')}
            className="flex items-center gap-1.5 rounded-full border border-primary-200 bg-white px-4 py-2 text-xs font-bold text-primary-600 shadow-sm transition hover:bg-primary-600 hover:text-white sm:px-5 sm:py-2.5 sm:text-sm"
          >
            Shop All
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Swiper */}
        {loading ? (
          <div className="rounded-3xl border border-gray-100 bg-white">
            <Loader compact label="Loading priority products" sublabel="Fetching today's highlighted products." />
          </div>
        ) : (
          <div className="overflow-hidden">
            <Swiper
              modules={[Autoplay, Navigation]}
              spaceBetween={12}
              slidesPerView={2}
              autoplay={{ delay: 2500, disableOnInteraction: false }}
              navigation
              loop
              className="!overflow-hidden !pb-1"
              breakpoints={{
                480:  { slidesPerView: 3, spaceBetween: 12 },
                640:  { slidesPerView: 3, spaceBetween: 14 },
                768:  { slidesPerView: 4, spaceBetween: 14 },
                1024: { slidesPerView: 5, spaceBetween: 16 },
                1280: { slidesPerView: 6, spaceBetween: 16 },
              }}
            >
              {products.map((product) => (
                <SwiperSlide key={product.product_id} className="!h-auto">
                  <ProductCard product={product} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}
      </div>
    </section>
  );
};

export default TopPriorityProducts;
